/**
 * Fetches a replay for the player, ported from avrio-rio
 * (services/workspace/replay/loader.ts): every chunk the manifest lists,
 * each checked against its SHA-256; the events read into the model; each
 * voice session's audio joined into one playable file; and the screen frames
 * taken while the candidate was away. Everything is fetched up front: the
 * signed links only last five minutes.
 *
 * Verified by Avrio. The manifest's exact bytes come from `manifestUrl` (what
 * Rio signed at `complete`), and the chunk list is read from those bytes, not
 * from the copy beside them. The replay counts as verified only when Rio's
 * signature over those bytes checks out against the key pinned in this build
 * (verify.ts) and was made with the key whose id is pinned beside it, the
 * bytes read as the same manifest as the view's, they are the manifest of
 * the replay the page chose and, from the interview, of this job, and every
 * listed chunk loaded and matched its SHA-256. Anything less is not verified,
 * and says why: neutrally when something couldn't be checked (no key, another
 * key, no signature, a link that failed or expired), as a warning when
 * something doesn't match what was signed.
 *
 * The manifest also names the candidate (`internalCandidateId`), but it is
 * not checked against the page's candidate: the two are not provably the same
 * identifier. avrio-backend's own note (CandidateOwnInterviewService) found 2
 * of 7 staging interview records keyed on another form, and a share's
 * candidacy at another job has its own id.
 */
import { sha256Hex } from './format';
import { readManifest } from './manifest';
import { pinnedKeyId, pinnedPublicKey, verifySignature } from './verify';
import { buildReplay } from './model';
import type { ReplayData } from './model';
import type { ManifestChunk, ReplayManifest, WorkReplayView } from './types';

export interface ReplayAudio {
  session: number;
  startT: number;
  blob: Blob;
}

export interface ReplayFrameImage {
  n: number;
  blob: Blob;
}

/**
 * `verified`, or why not: `mismatch` (the signature, the manifest or a part
 * doesn't match what was signed, or the signed manifest is another replay's
 * or another job's), `incomplete` (a part didn't load, or the recording
 * doesn't read through), `unsigned`, `no_key` (this build has no key, or no
 * key id, to check with), `other_key` (signed with a key this build doesn't
 * check against), `no_record` (the signed bytes couldn't be read).
 */
export type VerificationState =
  | 'verified'
  | 'mismatch'
  | 'incomplete'
  | 'unsigned'
  | 'no_key'
  | 'other_key'
  | 'no_record';

export interface Verification {
  verified: boolean;
  state: VerificationState;
  /** Shared with this job from the candidate's interview for another one: the signed job is that one. */
  shared: boolean;
  /** SHA-256 of the manifest's exact bytes (what was signed), when they could be read. */
  manifestSha256: string | null;
  /** Chunks that loaded and matched, of those listed. */
  matched: number;
  total: number;
}

export interface LoadedReplay {
  replay: ReplayData;
  manifest: ReplayManifest;
  audio: ReplayAudio[];
  frames: ReplayFrameImage[];
  verification: Verification;
}

/** What the page asked for: the signed manifest must be this replay's, and, from the interview, this job's. */
export interface ExpectedReplay {
  assetId: string;
  jobId: number | string;
}

export interface LoadDeps {
  expected: ExpectedReplay;
  fetch?: (url: string) => Promise<{
    ok: boolean;
    status?: number;
    arrayBuffer: () => Promise<ArrayBuffer>;
  }>;
  sha256?: (bytes: Uint8Array) => Promise<string>;
  /** Called as each part arrives: `done` of `total` (the manifest counts as one). */
  onProgress?: (done: number, total: number) => void;
  /** The key and its id (ARN) to check Rio's signature with; by default the ones pinned in this build. */
  publicKey?: string | null;
  keyId?: string | null;
  subtle?: SubtleCrypto;
}

/** Screen frames are fetched this many at a time. */
const FRAME_FETCHES_AT_ONCE = 6;

export class ReplayLoadError extends Error {
  constructor() {
    super("The replay's recording couldn't be read.");
    this.name = 'ReplayLoadError';
  }
}

const sameChunks = (a: ManifestChunk[], b: ManifestChunk[]) => {
  const key = (c: ManifestChunk) => `${c.kind}:${c.n}:${c.bytes}:${c.sha256}`;
  const left = a.map(key).sort();
  const right = b.map(key).sort();
  return left.length === right.length && left.every((k, i) => k === right[i]);
};

/** JSON with every object's keys sorted, so two readings compare whatever their key order. */
const canonical = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object')
    return `{${Object.keys(value)
      .sort()
      .map(
        (k) =>
          `${JSON.stringify(k)}:${canonical((value as Record<string, unknown>)[k])}`,
      )
      .join(',')}}`;
  return JSON.stringify(value);
};

/**
 * The signed manifest and the view's copy say the same in every field this
 * app reads. (avrio-backend passes the view through as JSON it has parsed and
 * written again, so the copy is compared as read, not byte for byte.)
 */
const sameManifest = (signed: ReplayManifest, copy: ReplayManifest) =>
  sameChunks(signed.chunks, copy.chunks) &&
  canonical({ ...signed, chunks: [] }) === canonical({ ...copy, chunks: [] });

export async function loadReplay(
  view: WorkReplayView,
  deps: LoadDeps,
): Promise<LoadedReplay> {
  const doFetch =
    deps.fetch ?? ((url: string) => fetch(url, { credentials: 'omit' }));
  const sha256 = deps.sha256 ?? sha256Hex;
  const publicKey =
    deps.publicKey === undefined ? pinnedPublicKey() : deps.publicKey;
  const keyId = deps.keyId === undefined ? pinnedKeyId() : deps.keyId;
  const total = view.manifest.chunks.length + 1;
  let done = 0;
  const tick = () => {
    done += 1;
    deps.onProgress?.(done, total);
  };

  const getBytes = async (url: string): Promise<Uint8Array | null> => {
    try {
      const response = await doFetch(url);
      if (!response.ok) return null;
      return new Uint8Array(await response.arrayBuffer());
    } catch {
      return null;
    }
  };

  // The manifest's own bytes: what Rio signed.
  let manifestSha256: string | null = null;
  let signed: ReplayManifest | null = null;
  const record = view.manifestUrl ? await getBytes(view.manifestUrl) : null;
  if (record) {
    manifestSha256 = await sha256(record);
    try {
      signed = readManifest(
        JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(record)),
      );
    } catch {
      signed = null;
    }
  }
  tick();
  const manifest = signed ?? view.manifest;

  let matched = 0;
  let missing = false;
  let differs = false;
  const fetchChunk = async (
    chunk: ManifestChunk,
  ): Promise<Uint8Array | null> => {
    const url = view.urls.find((u) => u.n === chunk.n && u.kind === chunk.kind);
    const body = url ? await getBytes(url.url) : null;
    if (!body) missing = true;
    else if ((await sha256(body)) === chunk.sha256) matched += 1;
    else differs = true;
    tick();
    return body;
  };

  const byKind = (kind: string) =>
    manifest.chunks.filter((c) => c.kind === kind).sort((a, b) => a.n - b.n);

  const eventsChunks = byKind('events');
  const texts = await Promise.all(eventsChunks.map(fetchChunk));
  const decoder = new TextDecoder();
  const present = texts.filter((t): t is Uint8Array => t !== null);
  if (present.length === 0) throw new ReplayLoadError();
  const replay = buildReplay(
    present.map((t) => decoder.decode(t)),
    manifest.transcript,
  );

  const audioChunks = byKind('audio');
  const audioBodies = new Map<number, Uint8Array>();
  const audioParts = await Promise.all(audioChunks.map(fetchChunk));
  audioChunks.forEach((c, i) => {
    const body = audioParts[i];
    if (body) audioBodies.set(c.n, body);
  });
  const audio: ReplayAudio[] = [];
  for (const session of replay.voice) {
    const parts = session.chunks
      .map((n) => audioBodies.get(n))
      .filter((p): p is Uint8Array => p !== undefined);
    if (parts.length) {
      audio.push({
        session: session.session,
        startT: session.startT,
        blob: new Blob(parts as BlobPart[], { type: 'audio/webm' }),
      });
    }
  }

  const frames: ReplayFrameImage[] = [];
  const screenChunks = byKind('screen');
  for (let i = 0; i < screenChunks.length; i += FRAME_FETCHES_AT_ONCE) {
    const batch = screenChunks.slice(i, i + FRAME_FETCHES_AT_ONCE);
    const bodies = await Promise.all(batch.map(fetchChunk));
    batch.forEach((c, j) => {
      const body = bodies[j];
      if (body)
        frames.push({
          n: c.n,
          blob: new Blob([body as BlobPart], { type: 'image/jpeg' }),
        });
    });
  }

  // Anything else the manifest lists is checked too, so "every chunk" means every chunk.
  const known = new Set(['events', 'audio', 'screen']);
  await Promise.all(
    manifest.chunks.filter((c) => !known.has(c.kind)).map(fetchChunk),
  );

  const chunkTotal = manifest.chunks.length;
  // Checked only when the signature names the pinned key: one made with
  // another key (another environment's, or a rotated one) can't be checked
  // here, which is not the same as one that failed.
  const pinned = !!publicKey && !!keyId;
  const ourKey = pinned && view.signature?.keyId === keyId;
  const signature =
    view.signature && ourKey && record
      ? await verifySignature(
          record,
          view.signature.value,
          publicKey!,
          deps.subtle,
        )
      : null;
  const { expected } = deps;
  const jobId = String(expected.jobId);
  // A share carries the candidate's interview for another job: its signed job
  // is that one. Any other grant must be this job's own interview.
  const share = view.grantType === 'share';
  // The replay the page chose, and from this job's interview (or shared).
  const theOneAsked =
    view.assetId === expected.assetId &&
    (signed === null ||
      (signed.assetId === expected.assetId &&
        (share || signed.jobId === jobId)));
  // A part that loaded but doesn't match, a signature that doesn't check
  // out, signed bytes that aren't the view's manifest, or another replay's:
  // not what was signed.
  const mismatched =
    differs ||
    signature === 'invalid' ||
    !theOneAsked ||
    (record !== null &&
      (signed === null || !sameManifest(signed, view.manifest)));
  let state: VerificationState;
  if (mismatched) state = 'mismatch';
  else if (missing || replay.damaged) state = 'incomplete';
  else if (!view.signature) state = 'unsigned';
  else if (!pinned || signature === 'no_key') state = 'no_key';
  else if (!ourKey) state = 'other_key';
  else if (!record) state = 'no_record';
  else state = signature === 'valid' ? 'verified' : 'mismatch';

  return {
    replay,
    manifest,
    audio,
    frames,
    verification: {
      verified: state === 'verified',
      state,
      shared: share && signed !== null && signed.jobId !== jobId,
      manifestSha256,
      matched,
      total: chunkTotal,
    },
  };
}
